/**
 * Tiny observable store. The UI is plain DOM, so rather than a framework we
 * keep one state object and let views subscribe to the slices they care about.
 */

import type { Fix, NavProgress, Place, Route } from './types';
import { loadSettings, saveSettings, type Settings } from './settings';

export type AppMode = 'idle' | 'searching' | 'preview' | 'navigating';

export interface AppState {
  mode: AppMode;
  settings: Settings;
  fix: Fix | null;
  /** True once we've had at least one usable fix. */
  located: boolean;
  gpsError: string | null;

  destination: Place | null;
  routes: Route[];
  selectedRouteIndex: number;
  routeError: string | null;
  routing: boolean;

  progress: NavProgress | null;
  /** Rerouting in flight, used to show a subtle indicator. */
  rerouting: boolean;

  /** Currently open overlay panel, if any. */
  panel: 'search' | 'settings' | 'trips' | 'spotify' | null;
  online: boolean;
}

type Listener = (state: AppState) => void;

function initialState(): AppState {
  return {
    mode: 'idle',
    settings: loadSettings(),
    fix: null,
    located: false,
    gpsError: null,
    destination: null,
    routes: [],
    selectedRouteIndex: 0,
    routeError: null,
    routing: false,
    progress: null,
    rerouting: false,
    panel: null,
    online: navigator.onLine,
  };
}

class Store {
  private state: AppState = initialState();
  private listeners = new Set<Listener>();
  private flushQueued = false;

  get(): AppState {
    return this.state;
  }

  /** Shallow-merge a patch and notify subscribers on the next microtask. */
  set(patch: Partial<AppState>): void {
    let changed = false;
    for (const k of Object.keys(patch) as Array<keyof AppState>) {
      if (this.state[k] !== patch[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.state = { ...this.state, ...patch };
    this.scheduleFlush();
  }

  /** Patch settings and persist. */
  patchSettings(patch: Partial<Settings>): void {
    const settings: Settings = {
      ...this.state.settings,
      ...patch,
      route: { ...this.state.settings.route, ...(patch.route ?? {}) },
    };
    saveSettings(settings);
    this.state = { ...this.state, settings };
    this.scheduleFlush();
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private scheduleFlush(): void {
    if (this.flushQueued) return;
    this.flushQueued = true;
    queueMicrotask(() => {
      this.flushQueued = false;
      const snapshot = this.state;
      for (const fn of this.listeners) {
        try {
          fn(snapshot);
        } catch (err) {
          console.error('[store] listener failed', err);
        }
      }
    });
  }
}

export const store = new Store();

/** Subscribe to a derived slice, firing only when the slice identity changes. */
export function select<T>(
  pick: (s: AppState) => T,
  fn: (value: T, state: AppState) => void,
  { immediate = true }: { immediate?: boolean } = {},
): () => void {
  let prev = pick(store.get());
  if (immediate) fn(prev, store.get());
  return store.subscribe((s) => {
    const next = pick(s);
    if (next !== prev) {
      prev = next;
      fn(next, s);
    }
  });
}

window.addEventListener('online', () => store.set({ online: true }));
window.addEventListener('offline', () => store.set({ online: false }));
